import { config } from '../../../config';
import type { NotificationKind } from '../../api/DigiobcanApi';
import {
  useIsApproverSomewhere,
  useIsInAssigningCategory,
  useRequiresApprovalSomewhere,
} from '../../features/queries/meGetReportCategories';
import { __ } from '../../utils';
import { useAuthRoles } from '@presov.sk/presov-auth-react';
import {
  Box,
  Stack,
  SwitchField,
  Typography,
} from '@presov.sk/presov-ui-react';
import React from 'react';

type KindOption = {
  kind: NotificationKind;
  label: string;
  description: string;
};

type KindGroup = { title: string; options: KindOption[] };

const CITIZEN: KindOption[] = [
  {
    kind: 'report-mine',
    label: __('Moje podnety'),
    description: __(
      'Zaevidovanie, prijatie, vyjadrenie úradu a vyriešenie podnetov, ktoré ste podali.',
    ),
  },
  {
    kind: 'voting-new',
    label: __('Nové hlasovania'),
    description: __('Keď sa otvorí nové hlasovanie.'),
  },
  {
    kind: 'voting-closed',
    label: __('Skončené hlasovania'),
    description: __('Keď skončí hlasovanie, v ktorom ste hlasovali.'),
  },
  {
    kind: 'news',
    label: __('Novinky'),
    description: __(
      'Novinky z kategórií, ktoré odoberáte v Preferenciách noviniek.',
    ),
  },
  {
    kind: 'gamification',
    label: __('Body'),
    description: __('Keď vám pribudnú body.'),
  },
];

type Visibility = {
  officer: boolean;
  approver: boolean;
  assigning: boolean;
  requiresApproval: boolean;
};

/**
 * Úradnícke druhy a kto ich vidí. Ponúka sa len to, čo môže človeku naozaj
 * prísť - prepínač na správy, ktoré nikdy nedostane, by len mátol
 * (moj-presov#28: „neprihlásený nebude môcť ani vidieť nastavenie o požiadavke
 * na schválenie").
 */
const OFFICER: (KindOption & { visible: (v: Visibility) => boolean })[] = [
  {
    kind: 'report-new',
    label: __('Nové podnety v mojich kategóriách'),
    description: __(
      'Nový podnet alebo možná duplicita v kategórii, za ktorú zodpovedáte.',
    ),
    visible: (v) => v.officer,
  },
  {
    kind: 'report-changes',
    label: __('Zmeny nálezov v mojich kategóriách'),
    description: __(
      'Keď kolega nález prijme, vyrieši, presunie, odstráni alebo zlúči.',
    ),
    visible: (v) => v.officer,
  },
  {
    kind: 'report-assigned',
    label: __('Pridelenie podnetov'),
    description: __('Keď vám nález pridelia alebo pridelenie zrušia.'),
    visible: (v) => v.assigning,
  },
  {
    kind: 'report-approval',
    label: __('Žiadosti o schválenie'),
    description: __(
      'Vyjadrenia a ich úpravy, ktoré čakajú na vaše schválenie, a kto o nich rozhodol.',
    ),
    visible: (v) => v.approver,
  },
  {
    kind: 'report-my-statements',
    label: __('Rozhodnutia o mojich vyjadreniach'),
    description: __(
      'Keď vaše vyjadrenie alebo jeho úpravu schvália, vrátia alebo stiahnu.',
    ),
    visible: (v) => v.requiresApproval,
  },
  {
    kind: 'report-roles',
    label: __('Moje roly v kategóriách'),
    description: __(
      'Menovanie do kategórie, zmena vašich rolí a zrušenie kategórie.',
    ),
    visible: (v) => v.officer,
  },
];

const useKindGroups = (): KindGroup[] => {
  const { hasPermission } = useAuthRoles();
  const approver = useIsApproverSomewhere();
  const assigning = useIsInAssigningCategory();
  const requiresApproval = useRequiresApprovalSomewhere();

  const officer = React.useMemo(
    () =>
      hasPermission({
        client_id: config.CLIENT_ID,
        permission: 'pruser:reports:read',
      }),
    [hasPermission],
  );

  return React.useMemo(() => {
    const visibility = { officer, approver, assigning, requiresApproval };
    const officerOptions = OFFICER.filter((o) => o.visible(visibility));

    return [
      { title: __('Pre mňa'), options: CITIZEN },
      ...(officerOptions.length
        ? [{ title: __('Administrácia podnetov'), options: officerOptions }]
        : []),
    ];
  }, [officer, approver, assigning, requiresApproval]);
};

type Props = {
  muted: NotificationKind[];
  disabled: boolean;
  onChange: (muted: NotificationKind[]) => void;
};

/**
 * O čom chcem byť notifikovaný (moj-presov#28). Prepínač je zapnutý, keď druh **nie
 * je** vo vypnutých - server drží vypnuté, takže nový druh je zapnutý sám.
 * Vypnutý druh nevznikne vôbec, ani v zvončeku a histórii.
 */
const NotificationKinds: React.FC<Props> = ({ muted, disabled, onChange }) => {
  const groups = useKindGroups();

  const toggle = (kind: NotificationKind, on: boolean) =>
    onChange(on ? muted.filter((k) => k !== kind) : [...muted, kind]);

  return (
    <Stack gap={2}>
      {groups.map((group) => (
        <Stack key={group.title} gap={1}>
          <Typography variant='body2' fontWeight={600} color='text.secondary'>
            {group.title}
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gap: 1,
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(2, minmax(0, 1fr))',
              },
            }}
          >
            {group.options.map((option) => (
              <SwitchField
                key={option.kind}
                label={option.label}
                description={option.description}
                checked={!muted.includes(option.kind)}
                disabled={disabled}
                onChange={(on) => toggle(option.kind, on)}
              />
            ))}
          </Box>
        </Stack>
      ))}
    </Stack>
  );
};

export default NotificationKinds;
